import _classCallCheck from "@babel/runtime/helpers/classCallCheck";
import _createClass from "@babel/runtime/helpers/createClass";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
import { getRetryAfter, genAxiosError, DEFAULT_OPTIONS } from './helper';

/**
 * Internal circuit breaker options
 */
/**
 * Circuit Breaking to handle 429 & 5XX error
 *
 * https://learn.microsoft.com/en-us/azure/architecture/patterns/circuit-breaker
 * https://wiki.autodesk.com/pages/viewpage.action?spaceKey=SCAF&title=ACS+Identity+Client+Resiliency
 */
var Breaker = /*#__PURE__*/function () {
  /**
   * Constructor the docs circuit breaker
   */
  function Breaker(options, logger, key) {
    _classCallCheck(this, Breaker);
    // Merge options
    this.options = Object.assign({}, DEFAULT_OPTIONS, options);
    this.logger = logger;
    this.key = key;

    // setup basic options
    this.state = Breaker.CLOSED;
    this.openStateSustainTime = this.options.openSustainTimeInMs;

    // setup RPM control variables
    this.rpmRequestCount = 0;
    this.rpmStartTime = Date.now();
    this.rpmExpireInMs = 60 * 1000;

    // setup control variables
    this.resetVariables();
  }

  /**
   * Reset control variables
   */
  return _createClass(Breaker, [{
    key: "resetVariables",
    value: function resetVariables() {
      // Processing and success request count under HALF_OPEN state
      this.inFlightRequestCount = 0;
      this.successCount = 0;

      // Error request count under CLOSE state for 5XX error
      this.errorRequestCount = 0;
      this.errorStartTime = 0;

      // The time when the breaker is switched to OPEN state
      this.openStateStartTime = 0;
    }

    /**
     * Get rmp value and prefer API specific value first
     */
  }, {
    key: "getRPM",
    value: function getRPM(config) {
      if (!this.options.enableRPMControl) return Breaker.RPM_NA;
      var rpm = config.requestPerMinute || this.options.requestPerMinute;
      return typeof rpm === 'number' && rpm > 0 ? rpm : Breaker.RPM_NA;
    }

    /**
     * Check rmp in the past 60 seconds and switch status if needed
     */
  }, {
    key: "checkRPM",
    value: function checkRPM(config) {
      // Check status first
      var requestPerMinute = this.getRPM(config);
      if (requestPerMinute === Breaker.RPM_NA) return;
      if (Date.now() > this.rpmStartTime + this.rpmExpireInMs) {
        this.info("[RPM ".concat(requestPerMinute, "] Reset ").concat(this.rpmRequestCount, " to 0 after ").concat(this.rpmExpireInMs / 1000, " seconds."));
        this.rpmRequestCount = 0;
        this.rpmStartTime = Date.now();
      }
      this.rpmRequestCount++;

      // Switch to open state if exceed rpm limit
      if (this.rpmRequestCount >= requestPerMinute && this.state === Breaker.CLOSED) {
        this.switchToOpenState("[RPM] Switch to OPEN state on ".concat(this.rpmRequestCount, " / ").concat(requestPerMinute, " requests."), config === null || config === void 0 ? void 0 : config.headers);
      }
    }

    /**
     * When request is sent
     */
  }, {
    key: "processStart",
    value: function processStart(config) {
      // Check rpm before process request
      this.checkRPM(config);

      // Check state
      switch (this.state) {
        case Breaker.OPEN:
          var openEndTime = this.openStateStartTime + this.openStateSustainTime;
          if (Date.now() >= openEndTime) {
            this.info("Switched to HALF_OPEN state after ".concat(this.openStateSustainTime / 1000, " seconds."));
            this.switchToHalfOpenState();
            this.processOnHalfOpen(config);
          } else {
            this.error("Cancelled at OPEN state. Retry after ".concat(Math.round((openEndTime - Date.now()) / 1000), " seconds."));
            // Mock axios error with 429 status to let consumer aware of the state
            throw genAxiosError('Cancelled by Circuit Breaker at OPEN state', 'ERR_TOO_MANY_REQUESTS', config, this.make429Error((openEndTime - Date.now()) / 1000));
          }
          break;
        case Breaker.HALF_OPEN:
          this.processOnHalfOpen(config);
          break;
        default:
          break;
      }
    }

    /**
     * Handle success request
     */
  }, {
    key: "processEnd",
    value: function processEnd() {
      if (this.state === Breaker.HALF_OPEN) {
        this.successCount++;
        this.info("Success count: ".concat(this.successCount, " / ").concat(this.options.thresholdToCloseState, " (threshold)"));
        if (this.inFlightRequestCount > 0) this.inFlightRequestCount--;
        if (this.successCount >= this.options.thresholdToCloseState) {
          this.info("Switched to CLOSE state after ".concat(this.successCount, " success requests."));
          this.switchToCloseState();
        }
      }
    }

    /**
     * Handle errors on response
     */
  }, {
    key: "processError",
    value: function processError(_ref) {
      var status = _ref.status,
        headers = _ref.headers;
      var _this$options = this.options,
        openSustainTimeInMs = _this$options.openSustainTimeInMs,
        thresholdExpireInMs = _this$options.thresholdExpireInMs;
      switch (this.state) {
        case Breaker.CLOSED:
          if (status === 429) {
            // Set openStateSustainTime to retry-after header and open circuit immediately
            this.openStateSustainTime = getRetryAfter(headers['retry-after']) * 1000;
            this.switchToOpenState("[429] CLOSED to OPEN on 429 error. Retry after ".concat(this.openStateSustainTime / 1000, " seconds."), headers);
          } else if (status >= 500) {
            // reset to default period
            this.openStateSustainTime = openSustainTimeInMs;
            if (this.errorStartTime === 0) {
              this.errorStartTime = Date.now();
            } else if (Date.now() > this.errorStartTime + thresholdExpireInMs) {
              this.errorRequestCount = 0;
              this.errorStartTime = Date.now();
              this.info("Reset error count to 0 after ".concat(thresholdExpireInMs / 1000, " seconds under CLOSED state."));
            }
            this.errorRequestCount++;
            if (this.errorRequestCount >= this.options.thresholdInError) {
              // The request from the application is routed to the operation.
              // The proxy maintains a count of the number of recent failures,
              // and if the call to the operation is unsuccessful the proxy increments this count.
              // If the number of recent failures exceeds a specified threshold within a given time period,
              // the proxy is placed into the Open state.
              this.switchToOpenState("[5XX] CLOSED to OPEN on ".concat(this.errorRequestCount, " errors in ").concat(thresholdExpireInMs / 1000, " seconds."), headers);
            }
          } else {
            this.debug("Ignore ".concat(status, " error under CLOSED state."));
          }
          break;
        case Breaker.HALF_OPEN:
          var isTooManyRequest = status === 429;
          if (isTooManyRequest || status >= 500) {
            // Set openStateSustainTime to retry-after header
            this.openStateSustainTime = isTooManyRequest ? getRetryAfter(headers['retry-after']) * 1000 : openSustainTimeInMs;
            // Open circuit
            this.switchToOpenState("[".concat(isTooManyRequest ? '429' : '5XX', "] HALF_OPEN to OPEN on ").concat(status, " error. Retry after ").concat(this.openStateSustainTime / 1000, " seconds."), headers);
          } else {
            this.debug("Ignore ".concat(status, " error under HALF_OPEN state."));
          }
          break;
        default:
          break;
      }
    }

    /**
     * Process request on HALF_OPEN state
     */
  }, {
    key: "processOnHalfOpen",
    value: function processOnHalfOpen(config) {
      if (this.inFlightRequestCount < this.options.thresholdToCloseState) {
        this.inFlightRequestCount++;
        this.info("Request sent at HALF_OPEN state. ".concat(this.inFlightRequestCount, " in flight."));
      } else {
        this.error("Cancelled at HALF_OPEN state. Too many requests (".concat(this.inFlightRequestCount, ") in flight."));

        // Mock axios error with 429 status to let consumer aware of the state
        throw genAxiosError('Cancelled by Circuit Breaker at HALF_OPEN state', 'ERR_TOO_MANY_REQUESTS', config, this.make429Error(this.openStateSustainTime / 1000));
      }
    }

    /**
     * Set state to closed
     */
  }, {
    key: "switchToOpenState",
    value: function switchToOpenState(reason, headers) {
      this.state = Breaker.OPEN;
      this.resetVariables();

      // At this point the proxy starts a timeout timer,
      // when this timer expires the proxy is placed into the Half-Open state.
      this.openStateStartTime = Date.now();

      // Logger the reason
      this.notify(reason, headers);
    }

    /**
     * Set state to closed
     */
  }, {
    key: "switchToCloseState",
    value: function switchToCloseState() {
      this.state = Breaker.CLOSED;
      this.resetVariables();
    }

    /**
     * Set state to Half-Open
     */
  }, {
    key: "switchToHalfOpenState",
    value: function switchToHalfOpenState() {
      this.state = Breaker.HALF_OPEN;
      this.resetVariables();
    }

    /**
     * Generate 429 error once circuit breaker is under open or Half-Open  state
     */
  }, {
    key: "make429Error",
    value: function make429Error(retryInSecond) {
      return {
        status: 429,
        headers: {
          'retry-after': retryInSecond
        },
        data: {}
      };
    }

    /**
     * Log message at info level
     */
  }, {
    key: "info",
    value: function info() {
      var _this$logger;
      for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
        args[_key] = arguments[_key];
      }
      if (this.logger) (_this$logger = this.logger).info.apply(_this$logger, ["[Circuit Breaker] ".concat(this.key, ":")].concat(args));
    }

    /**
     * Log message at error level
     */
  }, {
    key: "error",
    value: function error(reason) {
      var _this$logger2;
      for (var _len2 = arguments.length, args = new Array(_len2 > 1 ? _len2 - 1 : 0), _key2 = 1; _key2 < _len2; _key2++) {
        args[_key2 - 1] = arguments[_key2];
      }
      if (this.logger) (_this$logger2 = this.logger).error.apply(_this$logger2, ["[Circuit Breaker] ".concat(this.key, ":"), reason].concat(args));
    }

    /**
     * Log message at debug level
     */
  }, {
    key: "debug",
    value: function debug() {
      var _this$logger3;
      for (var _len3 = arguments.length, args = new Array(_len3), _key3 = 0; _key3 < _len3; _key3++) {
        args[_key3] = arguments[_key3];
      }
      if (this.logger) (_this$logger3 = this.logger).debug.apply(_this$logger3, ["[Circuit Breaker] ".concat(this.key, ":")].concat(args));
    }

    /**
     * Try to notify the error for datadog or other monitoring, fallback to normal error log
     */
  }, {
    key: "notify",
    value: function notify(reason, headers) {
      var _this$logger4;
      var requestId = headers === null || headers === void 0 ? void 0 : headers['x-request-id'];
      if ((_this$logger4 = this.logger) !== null && _this$logger4 !== void 0 && _this$logger4.notify) {
        var data = {
          message: 'Open circuit breaker!',
          apiKey: this.key,
          reason: reason
        };
        // Append request id to error details for better tracking on API calling
        if (requestId) data.requestId = requestId;
        this.logger.notify(data);
      } else {
        this.error(reason, requestId ? ", Request ID: ".concat(requestId) : '');
      }
    }
  }]);
}();
_defineProperty(Breaker, "OPEN", 'OPEN');
_defineProperty(Breaker, "CLOSED", 'CLOSED');
_defineProperty(Breaker, "HALF_OPEN", 'HALF_OPEN');
_defineProperty(Breaker, "RPM_NA", -1);
export default Breaker;